import { useEffect, useState } from 'react';

/**
 * Returns `true` when the user has requested reduced motion at the OS level
 * (Windows: Settings > Accessibility > Visual effects > Animation; macOS:
 * System Settings > Accessibility > Display > Reduce motion; iOS/Android:
 * similar). Updates live if the preference changes mid-session.
 *
 * Use this anywhere you set `style.transition` / `style.animation` from JS —
 * CSS-defined `transition` values are already disabled by App.css's global
 * `prefers-reduced-motion: reduce` rule, but JS-set inline styles bypass it.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduce;
}
